export default {
  methods: {
    // -----------------------------
    // Utility: Build option map
    // -----------------------------
    build_option_map(choices = []) {
      return choices.reduce((map, c) => {
        map[String(c.choice || "").toLowerCase()] = c.id;
        return map;
      }, {});
    },

    // -----------------------------
    // Helpers for dependent question
    // -----------------------------
    _get_dep_question_data(dq, all_questions_map) {
      return this._get_question_by_dep_id(all_questions_map, dq);
    },

    _normalize_dep_lookup_key(value) {
      return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
    },

    _get_dep_value(dep_question_data, dep_option_map) {
      if (!dep_question_data) return null;
      return this.deserialize_answer(
        dep_question_data.answer,
        dep_question_data?.question_type?.question_type || "short_text",
        dep_option_map
      );
    },

    _build_options(dq, dependantType) {
      const isSelectable = ["multi_select", "single_select"].includes(
        String(dependantType || "").toLowerCase()
      );

      //IMPORTANT: value is TEXT (choice) so trigger_condition like "Online" matches
      return isSelectable && dq.multi_select_choices
        ? dq.multi_select_choices.map((c) => ({
            text: c.choice,
            value: c.choice,
            is_mandatory: Boolean(c.is_mandatory),
          }))
        : [];
    },

    _get_dep_id(dependent_question, question = null) {
      return [
        question?.section_question_id,
        dependent_question?.dependant_section_question_id,
        dependent_question?.dependant_question_id,
        question?.id,
      ].find((value) => value !== null && value !== undefined) ?? null;
    },

    _get_question_by_dep_id(all_questions_map, dependent_question) {
      const safe_lookup = all_questions_map || {};
      const meta_lookup = Reflect.get(safe_lookup, "__meta") || {};
      const label_key = this._normalize_dep_lookup_key(
        dependent_question?.dependant_question_text || dependent_question?.question_text
      );
      return [
        Reflect.get(safe_lookup, dependent_question?.dependant_section_question_id),
        Reflect.get(meta_lookup.byQuestionId || {}, dependent_question?.dependant_question_id),
        label_key ? Reflect.get(meta_lookup.byLabel || {}, label_key) : null,
      ].find(Boolean) || null;
    },

    _resolve_choices(question, dependent_question) {
      return question?.multi_select_choices || dependent_question?.multi_select_choices || [];
    },

    _resolve_raw_type(question, dependent_question) {
      return (
        question?.question_type?.question_type ||
        dependent_question?.dependant_question_type?.question_type ||
        "short_text"
      );
    },

    _resolve_answer(question, dependent_question) {
      return question?.answer ?? dependent_question?.answer ?? null;
    },

    _resolve_label(question, dependent_question) {
      return question?.question_text || dependent_question?.dependant_question_text || "";
    },

    _resolve_required(question, dependent_question) {
      return (question?.is_mandatory ?? dependent_question?.is_mandatory) === true;
    },

    _build_child_dependents(dep, all_questions_map, node_key) {
      return (dep?.dependant_questions || [])
        .map((child) => this.build_dependent_question(child, all_questions_map, node_key))
        .filter(Boolean);
    },

    // -----------------------------
    // Main: Build dependent question (RECURSIVE)
    // -----------------------------
    build_dependent_question(dependent_question, all_questions_map, parent_key = "root") {
      const question = this._get_question_by_dep_id(all_questions_map, dependent_question);
      const dep_id = this._get_dep_id(dependent_question, question);
      if (!dep_id) return null;

      const dep_section_question_id = (
        question?.section_question_id
        ?? dependent_question?.dependant_section_question_id
        ?? null
      );

      const choices = this._resolve_choices(question, dependent_question);
      const option_map = this.build_option_map(choices);

      const raw_type = this._resolve_raw_type(question, dependent_question);
      const answer = this._resolve_answer(question, dependent_question);
      const label = this._resolve_label(question, dependent_question);

      let value = this.deserialize_answer(answer, raw_type, option_map);
      const mapped_type = this.map_question_type(raw_type);

      if (
        typeof this.get_identity_fallback_for_question === 'function'
        && ['short_text', 'email', 'number'].includes(String(raw_type || '').toLowerCase())
        && (value === '' || value === null || value === undefined)
      ) {
        value = this.get_identity_fallback_for_question(label);
      }

      const node_key = `${parent_key}.${dep_id}`;

      const data = {
        node_key,
        label,
        model: `q_${dep_id}`,
        id: `q_${dep_id}`,
        section_question_id: dep_section_question_id,
        type: mapped_type,
        required: this._resolve_required(question, dependent_question),
        required_options: choices
          .filter((choice) => choice.is_mandatory)
          .map((choice) => choice.choice),
        trigger_condition: dependent_question?.trigger_condition ?? null,
        is_visible: false,
        options: choices.map((c) => ({
          text: c.choice,
          value: c.choice,
          is_mandatory: Boolean(c.is_mandatory),
        })),
        value,
        is_dependent: true,
        dependent_questions: this._build_child_dependents(dependent_question, all_questions_map, node_key),
      };
      
      return data;
    },





    // -----------------------------
    // Answer deserialization
    // -----------------------------
    deserialize_answer(answer, question_type, option_map) {
      if (!answer) {
        return question_type === "multi_select" || question_type === "checkboxes" ? [] : "";
      }
      const type_key = String(question_type || "").toLowerCase();
      const deserializer = this._get_deserializer(type_key);
      return deserializer(answer, option_map);
    },

    _get_deserializer(type_key) {
      const deserializers = {
        single_select: (answer) => this._deserialize_select(answer),
        radio: (answer) => this._deserialize_select(answer),
        multi_select: (answer) => this._deserialize_multi_select(answer),
        checkboxes: (answer) => this._deserialize_multi_select(answer),
        file: (answer) => this._deserialize_file(answer),
        pdf_file: (answer) => this._deserialize_file(answer),
        facial_verification: (answer) => this._deserialize_file(answer),
      };
      return Reflect.get(deserializers, type_key) || ((a) => a);
    },

    _deserialize_select(answer) {
      // Keep as text so it matches trigger_condition like "Yes"
      return answer ?? "";
    },

    _deserialize_multi_select(answer) {
      // Keep as array of text values
      if (!answer) return [];
      return String(answer)
        .split(",")
        .map(s => s.trim())
        .filter(Boolean);
    },


    _deserialize_file(answer) {
      if (!answer) return null;

      if (typeof answer === 'object') {
        if (answer.answer !== undefined || answer.answer_text !== undefined) {
          return this._deserialize_file(answer.answer ?? answer.answer_text);
        }

        const existing_url = answer.presigned_url || answer.original_url || answer.file_url || answer.url || '';
        const existing_name = answer.file_name || answer.name || this._get_file_name_from_url(existing_url) || '';

        return existing_url || existing_name || answer.file
          ? {
              ...answer,
              file_name: existing_name,
              original_url: answer.original_url || existing_url || null,
              presigned_url: answer.presigned_url || existing_url || null,
            }
          : null;
      }

      if (typeof answer === 'string') {
        const trimmed = answer.trim();
        if (!trimmed) return null;

        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
          try {
            return this._deserialize_file(JSON.parse(trimmed));
          } catch {
            // fall through and treat as plain string
          }
        }

        if (this._looks_like_file_url(trimmed)) {
          return {
            file_name: this._get_file_name_from_url(trimmed) || trimmed,
            original_url: trimmed,
            presigned_url: trimmed,
          };
        }

        return { file_name: trimmed };
      }

      return null;
    },

    _looks_like_file_url(value) {
      const text = String(value || '').trim();
      if (!text) return false;
      return text.startsWith('http://') || text.startsWith('https://') || text.startsWith('/media/') || text.startsWith('blob:') || text.includes('.s3.');
    },

    _get_file_name_from_url(url) {
      const raw_url = String(url || '').trim();
      if (!raw_url) return '';

      try {
        const pathname = new URL(raw_url).pathname;
        return decodeURIComponent(pathname.split('/').pop() || '');
      } catch {
        return decodeURIComponent((raw_url.split('?')[0].split('/').pop()) || '');
      }
    },

    // -----------------------------
    // Dependent question listeners
    // -----------------------------
    setup_dependent_question_listeners() {
      if (!this.safe_form_config.sections) return;

      this.safe_form_config.sections.forEach((section) => {
        section.fields.forEach((field) => {
          // watch top-level field
          if (field.dependent_questions?.length > 0) {
            this.$watch(
              () => Reflect.get(this.safe_form_data, field.model),
              (newValue) => {
                this.update_dependent_questions_visibility(field, newValue);
              },
              { deep: true }
            );

            // initial visibility
            this.update_dependent_questions_visibility(
              field,
              Reflect.get(this.safe_form_data, field.model)
            );
          }

          // ALSO watch each dependent question recursively
          this._watch_dependent_tree(field.dependent_questions || []);
        });
      });
    },

    _watch_dependent_tree(dep_list = []) {
      (dep_list || []).filter(Boolean).forEach((dependent_question) => {
        this.$watch(
          () => Reflect.get(this.safe_form_data, dependent_question.model),
          (newValue) => this.update_dependent_questions_visibility(dependent_question, newValue),
          { deep: true }
        );

        this.update_dependent_questions_visibility(dependent_question, Reflect.get(this.safe_form_data, dependent_question.model));

        if (dependent_question.dependent_questions?.length) {
          this._watch_dependent_tree(dependent_question.dependent_questions);
        }
      });
    },


    // -----------------------------
    // Visibility (RECURSIVE) + correct matching
    // -----------------------------
    update_dependent_questions_visibility(questionNode, currentValue) {
      if (!questionNode.dependent_questions?.length) return;

      questionNode.dependent_questions.forEach((child) => {
        const shouldShow = this.does_value_match_trigger(
          currentValue,
          this.resolve_child_trigger_condition(questionNode, child),
          questionNode //pass CURRENT node (its options)
        );

        child.is_visible = shouldShow;

        // if parent hides, hide all descendants too
        if (!shouldShow && child.dependent_questions?.length) {
          this._hide_descendants(child);
        }

        // if parent shows, re-evaluate child’s children based on current child value
        if (shouldShow && child.dependent_questions?.length) {
          const childValue = Reflect.get(this.safe_form_data, child.model);
          this.update_dependent_questions_visibility(child, childValue);
        }
      });
    },

    _hide_descendants(node) {
      (node.dependent_questions || []).forEach((c) => {
        c.is_visible = false;
        if (c.dependent_questions?.length) this._hide_descendants(c);
      });
    },

    does_value_match_trigger(value, triggerCondition) {
      if (!triggerCondition) return false;

      // Since we set option.value = option.text, the value will already be "Yes"/"Online"/etc.
      if (Array.isArray(value)) {
        return value.some(
          (v) => this.normalize_value(v) === this.normalize_value(triggerCondition)
        );
      }

      return this.normalize_value(value) === this.normalize_value(triggerCondition);
    },

    resolve_child_trigger_condition(parentNode, childNode) {
      const child_label = this.normalize_value(childNode?.label);
      const matching_option = (parentNode?.options || []).find((option) => {
        const option_text = this.normalize_value(option?.text);
        const option_value = this.normalize_value(option?.value);
        return child_label && (child_label === option_text || child_label === option_value);
      });

      if (matching_option) {
        return matching_option.value || matching_option.text;
      }

      return childNode?.trigger_condition;
    },
  },
};
